import React, { useEffect, useState } from 'react'
import {
  Button,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import { Form, Row, Col } from 'react-bootstrap'

import API from '../../utils/API'

const DEFAULT_VIDEO_CALL_FORM = {
  videoCallLink: '',
}

const formatVideoCallResponse = (payload = {}) => {
  const { videoCallLink = '' } = payload || {}
  return {
    videoCallLink: videoCallLink || '',
  }
}

const validateVideoCallLink = (url) => {
  if (!url || url.trim() === '') {
    return { isValid: true, error: '' }
  }

  const trimmedUrl = url.trim()
  const zoomPattern = /^https?:\/\/([a-z0-9-]+\.)?zoom\.us\/j\/.+$/i
  const meetPattern = /^https?:\/\/meet\.google\.com\/[a-z-]+$/i

  if (zoomPattern.test(trimmedUrl) || meetPattern.test(trimmedUrl)) {
    return { isValid: true, error: '' }
  }

  return {
    isValid: false,
    error: 'Please enter a valid Meet link',
  }
}

const VideoCallingTab = ({ isZoomConnected = false }) => {
  const [videoCallForm, setVideoCallForm] = useState(DEFAULT_VIDEO_CALL_FORM)
  const [initialVideoCallForm, setInitialVideoCallForm] = useState(
    DEFAULT_VIDEO_CALL_FORM,
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [videoCallLinkError, setVideoCallLinkError] = useState('')

  const Error = useErrorService()
  const Notification = useNotificationService()

  useEffect(() => {
    const fetchVideoCallLink = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.AdminAPI.scheduling.getCoachCalendarLimits()
        const formatted = formatVideoCallResponse(data)
        setVideoCallForm(formatted)
        setInitialVideoCallForm(formatted)
      } catch (error) {
        Error.showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchVideoCallLink()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFieldChange = (field, value) => {
    setVideoCallForm((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (field === 'videoCallLink' && videoCallLinkError) {
      setVideoCallLinkError('')
    }
  }

  const buildPayload = () => {
    return {
      videoCallLink: videoCallForm.videoCallLink?.trim() || '',
    }
  }

  const handleSubmit = async () => {
    const validation = validateVideoCallLink(videoCallForm.videoCallLink)
    if (!validation.isValid) {
      setVideoCallLinkError(validation.error)
      return
    }

    setIsSubmitting(true)
    try {
      const payload = buildPayload()
      const { data } = await API.AdminAPI.scheduling.updateCoachCalendarLimits(
        payload,
      )
      const formatted = formatVideoCallResponse(data)
      setVideoCallForm(formatted)
      setInitialVideoCallForm(formatted)
      setVideoCallLinkError('')
      Notification.showNotification(
        'Video call link updated successfully.',
        'success',
      )
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setVideoCallForm(initialVideoCallForm)
    setVideoCallLinkError('')
  }

  const isFormDisabled = isLoading || isSubmitting

  return (
    <div>
      <p className='s6 text-muted mb-0'>
        Add a meeting link for your sessions, connect Zoom above, or do both.
        When Zoom is connected, its meetings are used first and this link is not
        used.
      </p>
      <>
        <Form>
          <Row>
            <Col md={12}>
              <h3 className='mb-3 mt-2 s3'>Video Call Link</h3>
              <Form.Group className='mb-4'>
                {/* <Form.Label className='input-lable'>Video Call Link</Form.Label> */}
                <Form.Control
                  type='url'
                  size='lg'
                  className={`form-control limits-form-control ${
                    videoCallLinkError ? 'is-invalid' : ''
                  }`}
                  placeholder='Enter Zoom or Google Meet URL'
                  value={videoCallForm.videoCallLink}
                  onChange={(event) =>
                    handleFieldChange('videoCallLink', event.target.value)
                  }
                  disabled={isFormDisabled}
                />
                {videoCallLinkError && (
                  <Form.Control.Feedback type='invalid' className='d-block'>
                    {videoCallLinkError}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <div className='d-flex justify-content-end mt-5'>
            <Button
              variant='outline-dark'
              className='mr-3 text-uppercase s8'
              onClick={handleCancel}
              disabled={isFormDisabled}>
              Cancel
            </Button>
            <Button
              variant='saffron'
              className='save-btn-fixed-width'
              onClick={handleSubmit}
              isLoading={isSubmitting || isLoading}
              disabled={isFormDisabled}>
              Save
            </Button>
          </div>
        </Form>
      </>
    </div>
  )
}

export default VideoCallingTab
